
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface SettingsFormProps {
  onSettingAdded: () => void;
}

export function SettingsForm({ onSettingAdded }: SettingsFormProps) {
  const [variableName, setVariableName] = useState("");
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Convert value to integer
    const intValue = parseInt(value, 10);
    if (isNaN(intValue)) {
      toast({
        title: "Error",
        description: "Value must be a valid number",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase
        .from('settings')
        .insert([{ variable_name: variableName, value: intValue }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Setting added successfully",
      });
      
      setVariableName("");
      setValue("");
      onSettingAdded();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4">Add New Setting</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="variableName" className="block text-sm font-medium text-gray-700">
            Variable Name
          </label>
          <input
            id="variableName"
            type="text"
            value={variableName}
            onChange={(e) => setVariableName(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            required
          />
        </div>
        <div>
          <label htmlFor="value" className="block text-sm font-medium text-gray-700">
            Value
          </label>
          <input
            id="value"
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            required
          />
        </div>
      </div>
      <Button type="submit" disabled={loading}>
        {loading ? "Adding..." : "Add Setting"}
      </Button>
    </form>
  );
}
